// frontend/src/components/Auth/Registration/UsernameStep.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../../api/client';
import './UsernameStep.css';

const UsernameStep = () => {
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [selectedUsername, setSelectedUsername] = useState('');
    const [customUsername, setCustomUsername] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [usernameAvailable, setUsernameAvailable] = useState(false);
    const [floatingParticles, setFloatingParticles] = useState([]);
    const [characterLevel] = useState(1);
    const [xpGained, setXpGained] = useState(0);

    // Generate floating particles
    useEffect(() => {
        const particles = [];
        for (let i = 0; i < 40; i++) {
            particles.push({
                id: i,
                left: Math.random() * 100,
                delay: Math.random() * 10,
                duration: 5 + Math.random() * 12,
                size: 2 + Math.random() * 8,
                opacity: 0.2 + Math.random() * 0.5
            });
        }
        setFloatingParticles(particles);
    }, []);

    const generateUsernames = async () => {
        if (!name || name.length < 2) {
            setError('⚔️ Hero, your name must be at least 2 characters!');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await authAPI.usernameStep({ name });
            if (response.success) {
                setSuggestions(response.suggestions);
                setShowSuggestions(true);
                setXpGained(50);
                setTimeout(() => setXpGained(0), 2000);
            } else {
                setError(response.message || 'Failed to generate usernames');
            }
        } catch (err) {
            setError('🌐 Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const selectUsername = (username) => {
        setSelectedUsername(username);
        setCustomUsername('');
        setUsernameAvailable(true);
    };

    const checkCustomUsername = (username) => {
        setCustomUsername(username);
        
        if (username.length < 3) {
            setUsernameAvailable(false);
            return;
        }
        
        if (!/^[a-zA-Z0-9_]+$/.test(username)) {
            setUsernameAvailable(false);
            return;
        }
        
        const isAvailable = !suggestions.includes(username);
        setUsernameAvailable(isAvailable);
    };

    const useCustomUsername = () => {
        if (customUsername && usernameAvailable) {
            setSelectedUsername(customUsername);
            setXpGained(25);
            setTimeout(() => setXpGained(0), 1500);
        }
    };

    const continueToNext = async () => {
        if (!selectedUsername) {
            setError('🎮 Choose your hero name before proceeding!');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await authAPI.selectUsername({ username: selectedUsername });
            if (response.success) {
                navigate('/email-verification');
            } else {
                setError(response.message || 'Username already taken');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="username-container">
            {/* Floating particles */}
            <div className="particle-field">
                {floatingParticles.map(particle => (
                    <div
                        key={particle.id}
                        className="particle"
                        style={{
                            left: `${particle.left}%`,
                            top: `${Math.random() * 100}%`,
                            width: `${particle.size}px`,
                            height: `${particle.size}px`,
                            animationDelay: `${particle.delay}s`,
                            animationDuration: `${particle.duration}s`,
                            opacity: particle.opacity
                        }}
                    />
                ))}
            </div>

            {/* Glowing orbs */}
            <div className="glow-orb orb-1"></div>
            <div className="glow-orb orb-2"></div>
            <div className="glow-orb orb-3"></div>

            {/* XP Toast */}
            {xpGained > 0 && (
                <div className="xp-toast">
                    <i className="fas fa-star"></i> +{xpGained} XP
                </div>
            )}

            <div className="username-card">
                <div className="username-header">
                    <div className="header-icon">
                        <i className="fas fa-user-astronaut"></i>
                        <div className="level-badge">
                            <i className="fas fa-crown"></i> Lvl {characterLevel}
                        </div>
                    </div>
                    <h2>Forge Your Legend</h2>
                    <p>Every great hero needs a legendary name!</p>
                    <div className="quest-progress">
                        <span className="quest-step">🏆 Quest 1/5</span>
                        <div className="mini-progress">
                            <div className="mini-progress-fill" style={{ width: '20%' }}></div>
                        </div>
                    </div>
                </div>

                <div className="username-body">
                    {!showSuggestions ? (
                        // Name Input Step
                        <>
                            <div className="hero-intro">
                                <div className="hero-avatar">
                                    <i className="fas fa-hat-wizard"></i>
                                </div>
                                <p className="hero-text">State thy name, brave adventurer!</p>
                            </div>

                            <div className="form-group">
                                <label className="floating-label">
                                    <i className="fas fa-feather-alt"></i> Your Hero Name
                                </label>
                                <div className="name-input-wrapper">
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Enter your full name..."
                                        autoComplete="name"
                                        className="gamified-input"
                                    />
                                    <i className="fas fa-pen-fancy"></i>
                                </div>
                                <small className="hint-text">
                                    <i className="fas fa-magic"></i> We'll conjure awesome username suggestions!
                                </small>
                            </div>

                            {error && (
                                <div className="error-message">
                                    <i className="fas fa-dragon"></i> {error}
                                </div>
                            )}

                            <div className="progress-section">
                                <div className="progress-bar">
                                    <div className="progress-fill" style={{ width: '20%' }}></div>
                                </div>
                                <p className="progress-text">
                                    <i className="fas fa-compass"></i> Step 1 of 5 - The Naming Ceremony
                                </p>
                            </div>

                            <button
                                className="generate-btn"
                                onClick={generateUsernames}
                                disabled={loading}
                            >
                                {loading ? (
                                    <><i className="fas fa-spinner fa-spin"></i> Summoning Names...</>
                                ) : (
                                    <><i className="fas fa-wand-magic"></i> Generate Epic Usernames</>
                                )}
                            </button>
                        </>
                    ) : (
                        // Username Selection Step
                        <>
                            <div className="selection-header">
                                <div className="selection-icon">
                                    <i className="fas fa-crown"></i>
                                </div>
                                <h5>Choose Your Legendary Title</h5>
                                <p>Pick a name that will echo through history!</p>
                            </div>

                            <div className="suggestions-grid">
                                {suggestions.map((username, idx) => (
                                    <div
                                        key={idx}
                                        className={`suggestion-card ${selectedUsername === username ? 'selected' : ''}`}
                                        onClick={() => selectUsername(username)}
                                    >
                                        <div className="suggestion-icon">
                                            <i className={`fas ${idx === 0 ? 'fa-crown' : idx === 1 ? 'fa-dragon' : 'fa-feather'}`}></i>
                                        </div>
                                        <div className="suggestion-text">{username}</div>
                                        <div className="suggestion-rarity">
                                            {idx === 0 && <span className="rarity legendary">⚡ Legendary</span>}
                                            {idx === 1 && <span className="rarity epic">🌟 Epic</span>}
                                            {idx === 2 && <span className="rarity rare">✨ Rare</span>}
                                            {idx > 2 && <span className="rarity common">📜 Common</span>}
                                        </div>
                                        {selectedUsername === username && (
                                            <div className="check-mark">
                                                <i className="fas fa-check"></i>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="custom-section">
                                <div className="divider">
                                    <span>⚡ OR FORGE YOUR OWN ⚡</span>
                                </div>

                                <label className="custom-label">
                                    <i className="fas fa-hammer"></i> Create Unique Title
                                </label>
                                <div className="custom-input-wrapper">
                                    <input
                                        type="text"
                                        value={customUsername}
                                        onChange={(e) => checkCustomUsername(e.target.value)}
                                        placeholder="Enter custom username..."
                                        className="custom-input"
                                    />
                                    <button
                                        className={`check-btn ${usernameAvailable && customUsername ? 'active' : ''}`}
                                        onClick={useCustomUsername}
                                        disabled={!usernameAvailable || !customUsername}
                                    >
                                        <i className="fas fa-check"></i> Claim
                                    </button>
                                </div>
                                {customUsername && (
                                    <div className={`username-feedback ${usernameAvailable ? 'available' : 'unavailable'}`}>
                                        {usernameAvailable ? (
                                            <><i className="fas fa-check-circle"></i> Available! Click claim to equip</>
                                        ) : (
                                            <><i className="fas fa-times-circle"></i> Already taken or invalid</>
                                        )}
                                    </div>
                                )}
                                <div className="username-rules">
                                    <div className="rule">
                                        <i className="fas fa-check-circle"></i>
                                        <span>3-20 characters</span>
                                    </div>
                                    <div className="rule">
                                        <i className="fas fa-check-circle"></i>
                                        <span>Letters, numbers & underscores</span>
                                    </div>
                                    <div className="rule">
                                        <i className="fas fa-check-circle"></i>
                                        <span>No spaces</span>
                                    </div>
                                </div>
                            </div>

                            {error && (
                                <div className="error-message">
                                    <i className="fas fa-skull"></i> {error}
                                </div>
                            )}

                            <div className="action-buttons">
                                <button
                                    className={`continue-btn ${selectedUsername ? 'ready' : ''}`}
                                    onClick={continueToNext}
                                    disabled={!selectedUsername || loading}
                                >
                                    {loading ? (
                                        <><i className="fas fa-spinner fa-spin"></i> Entering Realm...</>
                                    ) : (
                                        <><i className="fas fa-dungeon"></i> Begin Adventure</>
                                    )}
                                </button>
                                <button
                                    className="back-btn"
                                    onClick={() => {
                                        setShowSuggestions(false);
                                        setSelectedUsername('');
                                        setCustomUsername('');
                                    }}
                                >
                                    <i className="fas fa-undo-alt"></i> Return to Naming
                                </button>
                            </div>

                            {selectedUsername && (
                                <div className="selected-badge">
                                    <i className="fas fa-star"></i> Equipped: <strong>{selectedUsername}</strong>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UsernameStep;